import type { PlanSaveConflict } from '@repo/front-end-utils';

/**
 * Thrown inside `runPersistPlan` when the server refused a plan save because the wizard's copy is
 * out of date or a launch is held by someone else (`plan-save-version-check`). Carries the sentence
 * the person reads, so it can travel the same path as any failed save.
 */
export class PlanSaveConflictError extends Error {
  constructor(
    message: string,
    readonly conflict: PlanSaveConflict,
  ) {
    super(message);
    this.name = 'PlanSaveConflictError';
  }
}

/**
 * The viewer's local `HH:MM`, deliberately short: the sentence says "at 10:07", where the draft tag's
 * `whenText` says the date as well. Same local clock, shorter form.
 * `null` for a missing or bad instant. */
const clock = (iso: string | null): string | null => {
  if (!iso) return null;
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return null;
  return at.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
};

/**
 * The sentence for a refused plan save. Four cases (spec R7, R11): someone else saved; this same
 * person saved from another window; the saver is unknown; a launch is held by someone.
 *
 * @param conflict   what the server said
 * @param myEmail    the signed-in person's email, to tell "you in another window" from "someone"
 */
export const planSaveConflictSentence = (
  conflict: PlanSaveConflict,
  myEmail: string | null | undefined,
): string => {
  if (conflict.code === 'launch_in_progress') {
    const who = conflict.launchedBy ?? 'Someone';
    return `${who} is launching this twin. Your last change was not saved. Reload once the launch has finished.`;
  }
  const at = clock(conflict.modifiedAt);
  const when = at ? ` at ${at}` : '';
  if (conflict.modifiedBy && myEmail && conflict.modifiedBy === myEmail) {
    return `You saved this plan in another window${when}. Your last change here was not saved. Reload to carry on from that version.`;
  }
  const who = conflict.modifiedBy ?? 'Someone else';
  return `${who} saved this plan${when}, after you opened it. Your last change was not saved. Reload to see their changes.`;
};
